<template lang="pug">
SideOverlaySlot(
  :isOpen="isOpen",
  title="REFERRAL NOTES"
  @closeOverlay="closeOverlay"
)
  template(#contentTop)
    section.patient-section
      p <strong>Patient: {{ getUserFormatted(providerReferral.patient) }} </strong>
  template(#scrollbox)
    .overlay-pairs.history-section
      .item(v-for="note in notes" :key="note.id")
        .new {{ note.status }}
        .date-user
          .date-user__line
          .date-user__txt {{ getNoteTitle(note) }}
        .pairs.columns
          .pair.column.is-half
            .pair-label Type
            .pair-txt <strong>{{ note.type}}</strong>
          .pair.column.is-half(v-if="note.createdBy")
            .pair-label Name of Staff
            .pair-txt <strong>  {{ getUserFormatted(note.createdBy) }}</strong>
          .pair.column.is-half(v-else)
            .pair-label Name of Community Care
            .pair-txt <strong> {{ (note.communityCareOrganization?.name) }} </strong>
        .pairs.columns
          .pair.column
            .pair-label Notes
            .pair-txt
              p {{ note.content }}
        .pairs.columns(v-if="note.file")
          .pair.column
            .pair-label File Download
            .pair-txt
              a(href="#" @click.prevent="preview(note.file.signedUrl)")
                |  {{ note.file.name2 }}
                base-icon(name="external-link")
        div(v-if="notificationCanBeMarked(note)")
          BaseBtn.md.green.mark-done(
            href="#", 
            @click.prevent="openMarkAsDone(note.id)"
            v-if=("!note.markOpen")
          ) MARK AS DONE
          div.mark-done-confirmations(v-else)
            BaseBtn.md.blue.margin-right(@click.prevent="markNoteAsCompleted(note.id)") Yes, Complete
            BaseBtn.md.redFill(@click.prevent="resetMarkedAsDone(note.id)") NO, CANCEL

  template(#footer)
    .flex.flex--space-between
      BaseBtn.x-lg.blue-outline(v-if="showAppointmentsFor == 'clinics' " @click="viewAppointment") VIEW REFERRAL
      BaseBtn.x-lg.blue-outline(v-else-if="showAppointmentsFor == 'hospitals' " @click="viewAppointment") VIEW REFERRAL
      BaseBtn.x-lg.blue-outline(v-else-if="showAppointmentsFor == 'community' " @click="viewAppointment") VIEW REFERRAL
      BaseBtn.x-lg.blue-outline(v-else @click="closeOverlay") VIEW REFERRAL
      BaseBtn.x-lg.blue(
        href="#",
        @click.prevent="openAddNewReferralNoteOverlay"
      ) ADD NEW NOTE

AddNewProviderReferralNoteOverlay(
  :isOpen="isAddNewReferralNoteOverlayOpen",
  :providerReferral="providerReferral"
  @closeOverlay="closeAddNewReferralNoteOverlay"
)
</template>

<script>
import { defineComponent } from "vue";

import SideOverlaySlot from "@/components/overlays/SideOverlaySlot";
import AddNewProviderReferralNoteOverlay from "@/components/overlays/side-overlays/AddNewProviderReferralNoteOverlay.vue";
import listPagesMixin from "@/mixins/listPagesMixin";

import { fetchNotification, putNotification } from "@/api/notifications.ts";

import { getUserFormatted, getNoteTitle, getFileSignedUrl } from "@/helpers";
import { preview } from "@/helpers/preview";

import { mapGetters } from "vuex";

import { apiStatus, apiStatusComputedFactory } from "@/api";
import { fetchFileById } from "@/api/filesApi";
const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;

export default defineComponent({
  components: {
    AddNewProviderReferralNoteOverlay,
    SideOverlaySlot,
  },
  mixins: [listPagesMixin],

  emits: [
    "closeOverlay",
    "fetchNotifications",
    "openViewAppointment",
    "fetchProviderPatientReferralNotifications",
  ],

  props: {
    hasNoNotes: {
      required: false,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    providerReferral: {
      required: false,
      type: Object,
    },
    showAppointment: {
      required: false,
    },
    showAppointmentsFor: {
      required: false,
      default: "",
      type: String,
    },
  },

  data() {
    return {
      getUserFormatted,
      getNoteTitle,
      isAddNewReferralNoteOverlayOpen: false,
      fetchNotificationStatus: IDLE,
      putProviderReferralNotificationsStatus: IDLE,
      markNoteAsCompletedStatus: IDLE,
      notes: null,
      runFirstTime: true,
    };
  },
  computed: {
    ...mapGetters(["getUserId", "getUserFullName", "getUserRoleContext"]),
  },
  methods: {
    viewAppointment() {
      this.$emit("openViewAppointment", this.providerReferral.id);
    },

    notificationCanBeMarked(notification) {
      let canBeMarked = false;
      if (notification.status === "New") {
        if (this.getUserRoleContext.communityCareOrganization) {
          canBeMarked = notification.origin === "Provider";
        } else {
          canBeMarked = notification.origin === "Community";
        }
      }
      return canBeMarked;
    },

    async markNoteAsCompleted(notificationID) {
      this.markNoteAsCompleted = PENDING;
      try {
        const res = await putNotification(notificationID,{
          completedBy: this.getUserId,
          lastViewedBy: this.getUserId,
          status: "Completed",
        });
        this.markNoteAsCompleted = SUCCESS;
        this.$emit("fetchNotifications");
        this.$emit("closeOverlay");
        this.$store.dispatch("fetchNotificationCount");
      } catch (err) {
        this.markNoteAsCompleted = ERROR;
        console.error(err);
      }
    },

    async fetchNotification() {
      this.fetchNotificationStatus = PENDING;
      try {
        const res = await fetchNotification({
          params: {
            idEntity: this.providerReferral.id,
            entityType: "provider-referral",
            include: [
              "patientReferral",
              "patient",
              "completedBy",
              "lastViewedBy",
              "file",
              "createdBy",
            ],
          },
        });

        this.notes = await Promise.all(
          res.data.notifications.map(this.fetchFile)
        );
        if (!this.notes.length && this.runFirstTime) {
          this.isAddNewReferralNoteOverlayOpen = true;
          this.runFirstTime = false;
        }
        this.fetchNotificationStatus = SUCCESS;
      } catch (err) {
        this.fetchNotificationStatus = ERROR;
        console.error(err);
      }
    },

    async fetchFile(note) {
      if (note.file) {
        const { data } = await fetchFileById(note.file.id);
        const file = data.file;
        file.token = data.token;
        note.file.signedUrl = getFileSignedUrl(file);
      }

      return note;
    },

    preview(src) {
      preview(src);
    },

    closeOverlay() {
      this.$emit("closeOverlay");
    },
    openMarkAsDone(noteId) {
      this.notes.forEach((element) => {
        if (element.id == noteId) {
          element.markOpen = true;
        }
      });
    },
    resetMarkedAsDone(noteId) {
      this.notes.forEach((element) => {
        if (element.id == noteId) {
          element.markOpen = false;
        }
      });
    },
    openAddNewReferralNoteOverlay() {
      this.isAddNewReferralNoteOverlayOpen = true;
    },
    closeAddNewReferralNoteOverlay() {
      this.isAddNewReferralNoteOverlayOpen = false;
      this.$emit("fetchNotifications");
      this.fetchNotification();
      if (!this.notes.length) {
        this.closeOverlay();
      }
    },
  },
  created() {
    if (!this.providerReferral?.id || !this.isOpen) return;
    this.fetchNotification();
    if (this.hasNoNotes) {
      this.isAddNewReferralNoteOverlayOpen = true;
    }
  },
});
</script>

<style scoped lang="scss">
.mark-done-confirmations {
  margin-top: 9px;
  display: inline-block;
}
.margin-right {
  margin-right: 15px;
}
.patient-section {
  margin-bottom: 31px;
  padding: 37px 24px 25px;
  background-color: $primaryBgBlue;
  p {
    margin: 0 0 12px;
    font-size: 21px;
    font-weight: 500;
    line-height: 1.1;
    color: $primaryLightBlue;
    overflow: hidden;
    strong {
      font-weight: 700;
    }
  }
}
</style>
